import { photoCache } from "./media-cache";
import { track, reportError } from "./telemetry";
import { randomId } from "./random-id";
import { supabase, sessionUser } from "./supabase";
import { hasNativeUpload, uploadNative } from "./native-upload";
export function mediaKey(userId: string, name: string) {
  return `${userId}/media_${randomId()}.${
    name
      .split(".")
      .pop()
      ?.replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase() || "bin"
  }`;
}
/** Photos uploaded with a thumbnail end in `_t`; the thumbnail sits beside them as `<key>.thumb.jpg`
 * with the extension swapped, so its name alone says it exists. Older photos have none. */
export function photoKeys(userId: string, ext: "jpg" | "png") {
  const base = `${userId}/media_${randomId()}_t`;
  return { full: `${base}.${ext}`, thumb: `${base}.thumb.jpg` };
}
export const hasThumb = (key: string) => /_t\.(jpg|png)$/.test(key);
export const thumbKey = (key: string) => key.replace(/\.(jpg|png)$/, ".thumb.jpg");
export function validateMedia(file: Pick<File, "size" | "type">) {
  if (
    !/^(image\/(jpeg|png|webp|gif|heic|heif)|video\/(mp4|quicktime|webm)|audio\/(mpeg|mp4|aac|ogg|webm|wav|x-m4a))$/.test(
      file.type,
    )
  )
    throw new Error("Choose a supported photo, video, or audio file.");
  const limit = file.type.startsWith("image/") ? 10 : 25;
  if (file.size > limit * 1024 * 1024)
    throw new Error(`This file is too large. Choose a file under ${limit} MB.`);
}
async function sign(
  objectKey: string,
  contentType: string,
  operation: "get" | "put",
) {
  const { data, error } = await supabase.functions.invoke("media-sign", {
    body: { objectKey, contentType, operation },
  });
  if (error) throw error;
  if (!data?.url) throw new Error("Media service returned no upload URL");
  return data as { url: string; headers: Record<string, string> };
}
export type UploadOptions = {
  signal?: AbortSignal;
  onProgress?: (fraction: number) => void;
  /** Store under this key (it must be in the caller's own namespace). */
  objectKey?: string;
  /** The transfer is handed to the system uploader and continues if the app closes. */
  onQueued?: () => void;
};
async function performUpload(
  file: File,
  options: UploadOptions = {},
): Promise<{ objectKey: string; contentType: string }> {
  validateMedia(file);
  const user = await sessionUser();
  if (!user) throw new Error("Sign in before uploading");
  const contentType = file.type;
  const objectKey = options.objectKey ?? mediaKey(user.id, file.name);
  if (!objectKey.startsWith(`${user.id}/`)) throw new Error("Uploads must belong to you");
  const signed = await sign(objectKey, contentType, "put");
  if (options.signal?.aborted)
    throw new DOMException("Upload cancelled", "AbortError");
  if (hasNativeUpload()) {
    const uploadedKey = await uploadNative(file, { owner: user.id, objectKey, ...signed }, options);
    return { objectKey: uploadedKey, contentType };
  }
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const finish = (error?: Error) => {
      options.signal?.removeEventListener("abort", abort);
      error ? reject(error) : resolve();
    };
    xhr.open("PUT", signed.url);
    for (const [key, value] of Object.entries(signed.headers ?? {}))
      xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable)
        options.onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () =>
      finish(
        xhr.status >= 200 && xhr.status < 300
          ? undefined
          : new Error("Upload failed. Your draft is still available."),
      );
    xhr.onerror = () =>
      finish(new Error("Upload failed. Check your connection and retry."));
    xhr.onabort = () =>
      finish(new DOMException("Upload cancelled", "AbortError"));
    options.signal?.addEventListener("abort", abort, { once: true });
    xhr.send(file);
  });
  return { objectKey, contentType };
}
export async function uploadMedia(
  file: File,
  options: UploadOptions = {},
): Promise<{ objectKey: string; contentType: string }> {
  const media_type = file.type.startsWith("image/") ? "image"
    : file.type.startsWith("video/") ? "video"
    : file.type.startsWith("audio/") ? "audio" : "unsupported";
  const properties = { media_type, native: hasNativeUpload() };
  track("media_upload_attempted", properties);
  try {
    const result = await performUpload(file, options);
    track("media_upload_succeeded", properties);
    return result;
  } catch (error) {
    if (options.signal?.aborted || (error instanceof Error && error.name === "AbortError")) {
      track("media_upload_cancelled", properties);
    } else {
      track("media_upload_failed", properties);
      reportError(error, "media_upload", properties);
    }
    throw error;
  }
}
export async function uploadAvatar(file: File): Promise<string> {
  return (await uploadMedia(file)).objectKey;
}
/** What a stored media key holds, from its extension. */
export function mediaKind(key: string): "video" | "audio" | "image" {
  if (/\.(mp4|mov|webm|m4v)(\?|$)/i.test(key)) return "video";
  if (/\.(mp3|m4a|aac|wav|ogg|weba)(\?|$)/i.test(key)) return "audio";
  return "image";
}
/** Forgets a cached photo that failed to display, so the next load fetches it fresh. */
export async function forgetMediaUrl(key: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (session) await photoCache.forget(session.user.id, key);
}
type Waiter = { resolve: (url: string) => void; reject: (error: unknown) => void };
type SignResult = { objectKey: string; url?: string; error?: string };
const SIGN_BATCH = 40;
let signQueue: Map<string, Waiter[]> | null = null;
/** Media a screen asks for in the same moment signs in one media-sign call instead of one each.
 * If a batch call fails, its keys fall back to single signing. */
export function signGet(key: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!signQueue) {
      signQueue = new Map();
      setTimeout(flushSignQueue, 10);
    }
    signQueue.set(key, [...(signQueue.get(key) ?? []), { resolve, reject }]);
  });
}
function flushSignQueue() {
  const waiting = signQueue!;
  signQueue = null;
  const keys = [...waiting.keys()];
  const settle = (key: string, outcome: Promise<string>) =>
    outcome.then(url => waiting.get(key)!.forEach(w => w.resolve(url)), error => waiting.get(key)!.forEach(w => w.reject(error)));
  const single = (key: string) => settle(key, sign(key, "application/octet-stream", "get").then(s => s.url));
  for (let i = 0; i < keys.length; i += SIGN_BATCH) {
    const chunk = keys.slice(i, i + SIGN_BATCH);
    if (chunk.length === 1) { void single(chunk[0]); continue; }
    void supabase.functions.invoke("media-sign", { body: { operation: "get", objectKeys: chunk } })
      .then(({ data, error }) => {
        if (error || !Array.isArray(data?.results)) throw error ?? new Error("Media service returned no URLs");
        const results = new Map((data.results as SignResult[]).map(r => [r.objectKey, r]));
        for (const key of chunk) {
          const result = results.get(key);
          void settle(key, result?.url ? Promise.resolve(result.url)
            : Promise.reject(Object.assign(new Error(result?.error ?? "Media service returned no URL"), { status: result?.error === "Media not accessible" ? 403 : 502 })));
        }
      })
      .catch(() => chunk.forEach(key => void single(key)));
  }
}
export async function signedMediaUrl(key: string): Promise<string> {
  if (!key) return "";
  if (/^https:\/\//.test(key)) return key;
  const getUrl = () => signGet(key);
  // Video/audio retain streaming and range requests; cache photos by immutable key.
  if (!/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(key)) return getUrl();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Sign in to view media");
  return photoCache.get(session.user.id, key, getUrl);
}
